import { useState } from 'react';
import type { JSX } from 'react';
import { I18nProvider } from './i18n';
import { KeyEntryScreen, type GateReason } from './components/KeyEntryScreen';
import { MainShell } from './components/MainShell';
import { deviceKey as getDeviceKeyFromUrl } from './lib/deviceKey';

/**
 * The gate, mirroring Android's `AppNav.kt`: you cannot reach the app without a
 * key, and losing the key puts you straight back here.
 *
 * `I18nProvider` sits outside it, wrapping both branches, because the gate needs
 * the language as much as the shell does — it is the one screen a device is
 * likely to be handed over already showing a language the operator does not
 * read, and its own language button is the only way out of that. Android gets
 * this from the process locale, which is set before any Activity exists; here it
 * is a provider, and it has to be above the branch or switching language at the
 * gate would not survive signing in.
 *
 * The key is **one piece of state**, seeded once from the URL. It used to be
 * `manualKey || urlKey`, and that fallback is why neither logout nor a `401`
 * could ever leave the dashboard: `getDeviceKeyFromUrl()` reads a module-level
 * cache that never becomes null, so clearing the manual key was undone by the
 * fallback on the very next render. Reading the URL in the initialiser instead
 * means the URL provisions the device (SPEC §10) without also pinning it.
 *
 * A key typed at the gate lives in this `useState` and nowhere else — never in
 * `localStorage`, never in the URL (SPEC §13 Never). Safari evicts a WebClip's
 * storage on its own schedule, so a persisted key both disappears when it is
 * needed and outlives a re-provisioning when it is not.
 */
export default function App(): JSX.Element {
  const [key, setKey] = useState<string | null>(() => getDeviceKeyFromUrl());

  // Why the operator is looking at the gate. `null` on a cold start; set when a
  // key was taken away, so the screen can say which of the two things happened
  // instead of presenting an empty field with no explanation.
  const [reason, setReason] = useState<GateReason | null>(null);

  return (
    <I18nProvider>
      {key === null ? (
        <KeyEntryScreen
          reason={reason}
          onKeySubmit={(next) => {
            setReason(null);
            setKey(next);
          }}
        />
      ) : (
        <MainShell
          // Remounts the whole session when the key changes. `useCheckLoop` keeps
          // its scheduler state in refs, and a new key on top of an old loop would
          // inherit the previous cycle's guards — including `revoked`, which would
          // stop the new key before it ever ran.
          key={key}
          deviceKey={key}
          // A 401 mid-run. Same handling as Android: drop the key and return to the
          // gate. Deliberately *not* a locked screen — this app has a key field, so
          // an operator standing in front of the device can fix it right now, and
          // freezing the UI would take that away for no gain.
          onKeyRevoked={() => {
            setReason('revoked');
            setKey(null);
          }}
          // Deliberate removal from Settings.
          onKeyRemoved={() => {
            setReason('removed');
            setKey(null);
          }}
        />
      )}
    </I18nProvider>
  );
}
